import React, { useEffect, useId, useRef, useState } from 'react';
import { cx } from '../../lib/cx';

/**
 * Small non-modal popover anchored to a trigger. Closes on outside click,
 * Escape (returning focus to the trigger) or when `close()` is called.
 *
 * `trigger({ open, toggle, props })` must spread `props` onto its button.
 * `children` may be a function `({ close }) => node`.
 */
export const Popover = ({ trigger, children, align = 'end', className, panelLabel }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div ref={wrapperRef} className="relative">
      {trigger({
        open,
        toggle: () => setOpen((v) => !v),
        props: {
          ref: triggerRef,
          'aria-expanded': open,
          'aria-controls': panelId,
          'aria-haspopup': 'dialog',
          onClick: () => setOpen((v) => !v),
        },
      })}
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={panelLabel}
          className={cx(
            'absolute top-full z-40 mt-2 w-72 animate-pop-in rounded-xl border border-line bg-surface p-1.5 text-fg shadow-overlay',
            align === 'end' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
            className,
          )}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      )}
    </div>
  );
};
